import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getAuditLogs } from "@/services/fraudDetection/db/adminOperations";
import { toast } from "@/hooks/use-toast";
import { usePagination } from "@/hooks/usePagination";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import PaginatedTable from "@/components/common/PaginatedTable";
import AuditLogCard from "@/components/admin/AuditLogCard";
import { logInfo, logError } from "@/utils/logger";

interface AuditLog {
  event_id: string;
  user_id: string;
  timestamp: string;
  action: string;
  details: string;
}

interface AuditLogsResponse {
  data: AuditLog[] | null;
  count: number;
  error: string | null;
  status: number;
}

const AuditLogs = () => {
  const { profile } = useAuth();
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");
  const { 
    currentPage, 
    pageSize, 
    setPage,
    setPageSize,
    nextPage,
    prevPage,
    pageCount
  } = usePagination({ 
    initialPage: 1, 
    initialPageSize: 10 
  });
  
  const [totalPages, setTotalPages] = useState(1);

  const { data: logsData, isLoading, error } = useQuery<AuditLogsResponse>({
    queryKey: ['auditLogs', profile?.id, currentPage, pageSize],
    queryFn: async (): Promise<AuditLogsResponse> => {
      logInfo("Fetching audit logs", "AuditLogs", { 
        adminId: profile?.id, 
        page: currentPage, 
        pageSize 
      });
      
      if (!profile?.id) throw new Error("Admin profile not found");
      
      const columns = 'event_id,user_id,timestamp,action,details';
      const response = await getAuditLogs(
        profile.id, 
        pageSize, 
        (currentPage - 1) * pageSize
      );
      
      if (response.error) {
        logError("Failed to fetch audit logs", "AuditLogs", { error: response.error });
        throw new Error(response.error);
      }
      
      logInfo("Successfully fetched audit logs", "AuditLogs", { 
        count: response.data?.length || 0,
        totalLogs: response.count || 0,
        status: response.status
      });

      return response as AuditLogsResponse;
    },
    enabled: !!profile?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (logsData?.count) {
      const newTotalPages = Math.ceil(logsData.count / pageSize);
      setTotalPages(newTotalPages);
    }
  }, [logsData?.count, pageSize]);

  useEffect(() => {
    if (error) {
      logError("AuditLogs query error", "AuditLogs", { error });
      toast({
        title: "Error",
        description: `Failed to load audit logs: ${error.message}`,
        variant: "destructive",
      });
    }
  }, [error]);

  const columns = [
    {
      key: "event_id",
      header: "Event ID",
      render: (item: AuditLog) => <span className="font-mono text-xs">{item.event_id}</span>
    },
    {
      key: "timestamp",
      header: "Timestamp",
      render: (item: AuditLog) => {
        try {
          return new Date(item.timestamp).toLocaleString();
        } catch (e) {
          return item.timestamp;
        }
      }
    },
    {
      key: "user_id",
      header: "User ID",
    },
    {
      key: "action",
      header: "Action",
      render: (item: AuditLog) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          item.action.includes('login') ? 'bg-blue-100 text-blue-800' :
          item.action.includes('create') ? 'bg-green-100 text-green-800' :
          item.action.includes('export') ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {item.action}
        </span>
      )
    },
    {
      key: "details",
      header: "Details",
    }
  ];

  if (isLoading) {
    return (
      <div className="container py-8">
        <Card>
          <CardHeader>
            <CardTitle>Loading Audit Logs...</CardTitle>
            <CardDescription>Please wait while we retrieve the audit trail</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-muted rounded animate-pulse"></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>System Audit Logs</CardTitle>
              <CardDescription>
                Complete audit trail of system activity
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => setViewMode("table")}>
                <span className="text-sm font-medium">Table View</span>
              </button>
              <button onClick={() => setViewMode("cards")}>
                <span className="text-sm font-medium">Card View</span>
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === "table" && logsData?.data && (
            <PaginatedTable
              data={logsData.data}
              columns={columns}
              totalItems={logsData.count || 0}
              currentPage={currentPage}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onItemsPerPageChange={setPageSize}
            />
          )}

          {viewMode === "cards" && logsData?.data && (
            <div className="space-y-4">
              {logsData.data.map((log) => (
                <AuditLogCard key={log.event_id} logItem={log} />
              ))}
            </div>
          )}

          {(!logsData?.data || logsData.data.length === 0) && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No audit logs found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
